import useActions from "@/actions/useActions";
import { useGeneralStore } from "@/stores/generalStore";
import { useMjmlStore } from "@/stores/mjmlStore";
import { usePreviewStore } from "@/stores/preview";
import { useFileManagerStore } from "@/stores/fileManger";
import { useCommentsStore } from "@/stores/commentsStore";
import { useNotificationsStore } from "@/stores/notifications";
import { useRolesStore } from "@/stores/roles";
import { getCopiedElement } from "@topol/utils";
import { extractImageFilesFromClipboard } from "@topol/image-picker";
import { useI18n } from "vue-i18n";
import { useClipboard } from "@vueuse/core";

import { onBeforeMount, onBeforeUnmount } from "vue";

export default () => {
  const mjmlStore = useMjmlStore();
  const previewStore = usePreviewStore();
  const generalStore = useGeneralStore();
  const fileManagerStore = useFileManagerStore();
  const commentStore = useCommentsStore();
  const notificationsStore = useNotificationsStore();
  const rolesStore = useRolesStore();
  const { t } = useI18n();
  const { copy } = useClipboard();

  const { save, saveAndClose } = useActions();

  const keydownHandler = async (e: KeyboardEvent) => {
    if (e === undefined || e.key === undefined) return;

    if (
      e.key.toLocaleLowerCase() === "s" &&
      !e.shiftKey &&
      (e.ctrlKey || e.metaKey)
    ) {
      e.preventDefault();
      save();
    }

    if (
      e.key.toLocaleLowerCase() === "s" &&
      e.shiftKey &&
      (e.ctrlKey || e.metaKey)
    ) {
      e.preventDefault();
      saveAndClose();
    }

    if (
      e.key.toLocaleLowerCase() === "z" &&
      !e.shiftKey &&
      (e.ctrlKey || e.metaKey)
    ) {
      e.preventDefault();
      mjmlStore.goBack();
    }

    if (
      e.key.toLocaleLowerCase() === "z" &&
      e.shiftKey &&
      (e.ctrlKey || e.metaKey)
    ) {
      e.preventDefault();
      mjmlStore.goForward();
    }

    if (
      e.key.toLocaleLowerCase() === "p" &&
      !e.shiftKey &&
      (e.ctrlKey || e.metaKey)
    ) {
      e.preventDefault();
      previewStore.tooglePreviewMode();
    }

    if (
      // czech layout english keyboard
      (e.key === "`" && e.shiftKey && (e.ctrlKey || e.metaKey)) ||
      // eglish layout english keyboard
      (e.key === "~" && !e.shiftKey && (e.ctrlKey || e.metaKey)) ||
      // czech layout czech keyboard
      (e.key === "\\" && !e.shiftKey && (e.ctrlKey || e.metaKey)) ||
      // czech windows layout
      (e.key === "'" && e.shiftKey && (e.ctrlKey || e.metaKey))
    ) {
      e.preventDefault();
      generalStore.developerModalOpened = true;
    }

    if (
      e.key === "Escape" &&
      !fileManagerStore.visibleFileManager &&
      !commentStore.openDetail
    ) {
      mjmlStore.selectedSection = null;
      mjmlStore.selectedBlock = undefined;
      const tinymceOpend = document.querySelector(".mce-edit-focus");
      if (tinymceOpend) {
        // @ts-expect-error
        tinymceOpend.blur();
      }
    }

    if ((e.metaKey || e.ctrlKey) && e.key.toLocaleLowerCase() === "c") {
      const activeElement = document.activeElement;
      if (
        activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "TEXTAREA"
      ) {
        return;
      }
      if (
        window.getSelection()?.toString() === "" &&
        (mjmlStore.selectedBlock || mjmlStore.selectedSection)
      ) {
        try {
          await navigator.clipboard.readText();
        } catch (e) {
          notificationsStore.createNotification({
            type: "error",
            text: t("layout.clipboard_not_allowed"),
          });
          return;
        }
        if (mjmlStore.selectedSection) {
          const section = mjmlStore.getSelectedSectionByIndex(
            mjmlStore.selectedSection.sectionIndex
          );
          if (
            section.attributes.locked &&
            !rolesStore.permissions.canEditLockedSections
          ) {
            return;
          }
          try {
            copy(JSON.stringify(section));
            if (isSafari()) {
              e.preventDefault();
            }
          } catch (e) {
            return;
          }
        }
        if (mjmlStore.selectedBlock) {
          const block = mjmlStore.getBlockByIndexes({
            sectionIndex: mjmlStore.selectedBlock.sectionIndex,
            columnIndex: mjmlStore.selectedBlock.columnIndex,
            blockIndex: mjmlStore.selectedBlock.blockIndex,
          });
          if (block.locked && !rolesStore.permissions.canEditLockedBlocks) {
            return;
          }
          try {
            copy(JSON.stringify(block));
            if (isSafari()) {
              e.preventDefault();
            }
          } catch (e) {
            return;
          }
        }
      }
    }
  };

  function onPaste(e: ClipboardEvent) {
    if (!e.clipboardData) {
      return;
    }

    if (extractImageFilesFromClipboard(e).length > 0) {
      const block = mjmlStore.getSelectedBlock;
      const isImageBlock =
        !!block && block.tagName === "mj-image" && !block.isVideo;
      if (!isImageBlock) {
        e.preventDefault();
      }
      return;
    }

    const data = getCopiedElement(e.clipboardData.getData("text"));

    if (!data) {
      return;
    }
    const activeElementTagName = document.activeElement?.tagName;
    const inputTagNames = ["INPUT", "TEXTAREA"];

    if (activeElementTagName && inputTagNames.includes(activeElementTagName)) {
      e.preventDefault();
      return;
    }
  }

  function isSafari() {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  onBeforeMount(() => {
    document.addEventListener("keydown", keydownHandler);
    document.addEventListener("paste", onPaste);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("keydown", keydownHandler);
    document.removeEventListener("paste", onPaste);
  });
};
